import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router';
import { Toaster } from 'sonner@2.0.3';
import { HelmetProvider } from 'react-helmet-async';
import { Layout } from './components/Layout';
import { DynamicPageRouter } from './components/DynamicPageRouter';
import { BackendStatusAlert } from './components/BackendStatusAlert';
import { GTMScript } from './components/GTMScript';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { getApiUrl } from './utils/apiConfig';
import { logger } from "./utils/logger";
import './utils/deploymentChecker';

// 🚀 LAZY LOADING - Components load only when routes are accessed
// FLEXIBLE PATTERN: Handles both named AND default exports automatically!

// Menu & Footer CMS
const MenuCMSEditor = lazy(() => import('./pages/MenuCMSEditor').then(m => ({ default: m.MenuCMSEditor || m.default })));
const FooterCMSEditor = lazy(() => import('./pages/FooterCMSEditor').then(m => ({ default: m.FooterCMSEditor || m.default })));
const LegalPagesCMSEditor = lazy(() => import('./pages/LegalPagesCMSEditor').then(m => ({ default: m.LegalPagesCMSEditor || m.default })));
const LegalPagesCleanup = lazy(() => import('./pages/LegalPagesCleanup').then(m => ({ default: m.LegalPagesCleanup || m.default })));
const TermsPageDebug = lazy(() => import('./pages/TermsPageDebug').then(m => ({ default: m.TermsPageDebug || m.default })));
const FullDiagnostics = lazy(() => import('./pages/FullDiagnostics').then(m => ({ default: m.FullDiagnostics || m.default })));
const TrackingSettingsDashboard = lazy(() => import('./pages/TrackingSettingsDashboard').then(m => ({ default: m.TrackingSettingsDashboard || m.default })));
const CustomScriptsManager = lazy(() => import('./pages/CustomScriptsManager').then(m => ({ default: m.CustomScriptsManager || m.default })));
const SEODashboard = lazy(() => import('./pages/SEODashboard').then(m => ({ default: m.SEODashboard || m.default })));
const PromoBarEditor = lazy(() => import('./pages/PromoBarEditor').then(m => ({ default: m.PromoBarEditor || m.default })));

// Video Examples CMS
const VideoExamplesDashboard = lazy(() => import('./pages/VideoExamplesDashboard').then(m => ({ default: m.VideoExamplesDashboard || m.default })));
const VideoExamplesPage = lazy(() => import('./pages/VideoExamplesPage').then(m => ({ default: m.VideoExamplesPage || m.default })));

// Admin & Setup
const AdminSetup = lazy(() => import('./pages/AdminSetup').then(m => ({ default: m.AdminSetup || m.default })));
const AdminLogin = lazy(() => import('./pages/AdminLogin').then(m => ({ default: m.AdminLogin || m.default })));
const AdminSignup = lazy(() => import('./pages/AdminSignup').then(m => ({ default: m.AdminSignup || m.default })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard || m.default })));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage').then(m => ({ default: m.UserManagementPage || m.default })));

// Homepage CMS
const HomepageDashboard = lazy(() => import('./pages/HomepageDashboard').then(m => ({ default: m.HomepageDashboard || m.default })));
const HeroEditor = lazy(() => import('./pages/HeroEditor').then(m => ({ default: m.HeroEditor || m.default })));
const TrustedClientsRedirect = lazy(() => import('./pages/TrustedClientsRedirect').then(m => ({ default: m.TrustedClientsRedirect || m.default })));
const HomepageLogoPreview = lazy(() => import('./pages/HomepageLogoPreview').then(m => ({ default: m.HomepageLogoPreview || m.default })));
const LogoEditor = lazy(() => import('./pages/LogoEditor').then(m => ({ default: m.LogoEditor || m.default })));
const WhyChooseUsRedirect = lazy(() => import('./pages/WhyChooseUsRedirect').then(m => ({ default: m.WhyChooseUsRedirect || m.default })));
const HomepageWhyChoosePreview = lazy(() => import('./pages/HomepageWhyChoosePreview').then(m => ({ default: m.HomepageWhyChoosePreview || m.default })));
const WhyChooseEditor = lazy(() => import('./pages/WhyChooseEditor').then(m => ({ default: m.WhyChooseEditor || m.default })));
const ServicesEditor = lazy(() => import('./pages/ServicesEditor').then(m => ({ default: m.ServicesEditor || m.default })));
const RealWorldImpactEditor = lazy(() => import('./pages/RealWorldImpactEditor').then(m => ({ default: m.RealWorldImpactEditor || m.default })));
const ROICalculatorEditor = lazy(() => import('./pages/ROICalculatorEditor').then(m => ({ default: m.ROICalculatorEditor || m.default })));
const HowWeWorkEditor = lazy(() => import('./pages/HowWeWorkEditor').then(m => ({ default: m.HowWeWorkEditor || m.default })));
const IndustryExpertiseEditor = lazy(() => import('./pages/IndustryExpertiseEditor').then(m => ({ default: m.IndustryExpertiseEditor || m.default })));
const BlogPreviewEditor = lazy(() => import('./pages/BlogPreviewEditor').then(m => ({ default: m.BlogPreviewEditor || m.default })));
const VideoPlannerEditor = lazy(() => import('./pages/VideoPlannerEditor').then(m => ({ default: m.VideoPlannerEditor || m.default })));
const HomepageFaqPreview = lazy(() => import('./pages/HomepageFaqPreview').then(m => ({ default: m.HomepageFaqPreview || m.default })));
const HomepageSectionOrderEditor = lazy(() => import('./pages/HomepageSectionOrderEditor').then(m => ({ default: m.HomepageSectionOrderEditor || m.default })));
const HomepageBundlePricingPreview = lazy(() => import('./pages/HomepageBundlePricingPreview').then(m => ({ default: m.HomepageBundlePricingPreview || m.default })));

// About Us Page
const AboutUsDashboard = lazy(() => import('./pages/AboutUsDashboard').then(m => ({ default: m.AboutUsDashboard || m.default })));
const AboutHeroEditor = lazy(() => import('./pages/AboutHeroEditor').then(m => ({ default: m.AboutHeroEditor || m.default })));
const AboutStoryOverviewEditor = lazy(() => import('./pages/AboutStoryOverviewEditor').then(m => ({ default: m.AboutStoryOverviewEditor || m.default })));
const AboutStoryJourneyEditor = lazy(() => import('./pages/AboutStoryJourneyEditor').then(m => ({ default: m.AboutStoryJourneyEditor || m.default })));
const AboutStoryMissionVisionEditor = lazy(() => import('./pages/AboutStoryMissionVisionEditor').then(m => ({ default: m.AboutStoryMissionVisionEditor || m.default })));
const AboutStoryCoreValuesEditor = lazy(() => import('./pages/AboutStoryCoreValuesEditor').then(m => ({ default: m.AboutStoryCoreValuesEditor || m.default })));
const AboutPeopleOverviewEditor = lazy(() => import('./pages/AboutPeopleOverviewEditor').then(m => ({ default: m.AboutPeopleOverviewEditor || m.default })));
const AboutPeopleExpertiseEditor = lazy(() => import('./pages/AboutPeopleExpertiseEditor').then(m => ({ default: m.AboutPeopleExpertiseEditor || m.default })));
const AboutPeopleCultureEditor = lazy(() => import('./pages/AboutPeopleCultureEditor').then(m => ({ default: m.AboutPeopleCultureEditor || m.default })));
const AboutTestimonialsPreview = lazy(() => import('./pages/AboutTestimonialsPreview').then(m => ({ default: m.AboutTestimonialsPreview || m.default })));
const AboutTestimonialsRedirect = lazy(() => import('./pages/AboutTestimonialsRedirect').then(m => ({ default: m.AboutTestimonialsRedirect || m.default })));
const AboutWorkProcessEditor = lazy(() => import('./pages/AboutWorkProcessEditor').then(m => ({ default: m.AboutWorkProcessEditor || m.default })));
const AboutWorkBenefitsEditor = lazy(() => import('./pages/AboutWorkBenefitsEditor').then(m => ({ default: m.AboutWorkBenefitsEditor || m.default })));
const AboutVideoPlannerPreview = lazy(() => import('./pages/AboutVideoPlannerPreview').then(m => ({ default: m.AboutVideoPlannerPreview || m.default })));
const AboutBlogPreview = lazy(() => import('./pages/AboutBlogPreview').then(m => ({ default: m.AboutBlogPreview || m.default })));
const AboutFaqPreview = lazy(() => import('./pages/AboutFaqPreview').then(m => ({ default: m.AboutFaqPreview || m.default })));
const AboutTrustedClientsRedirect = lazy(() => import('./pages/AboutTrustedClientsRedirect').then(m => ({ default: m.AboutTrustedClientsRedirect || m.default })));
const AboutLogoPreview = lazy(() => import('./pages/AboutLogoPreview').then(m => ({ default: m.AboutLogoPreview || m.default })));

// Blog CMS
const BlogDashboard = lazy(() => import('./pages/BlogDashboard').then(m => ({ default: m.BlogDashboard || m.default })));
const BlogEditorNew = lazy(() => import('./pages/BlogEditorNew').then(m => ({ default: m.BlogEditorNew || m.default })));
const TaxonomyManager = lazy(() => import('./pages/TaxonomyManager').then(m => ({ default: m.TaxonomyManager || m.default })));
const TestBlogBlocksFetch = lazy(() => import('./pages/TestBlogBlocksFetch').then(m => ({ default: m.TestBlogBlocksFetch || m.default })));
const FetchBestPracticesContent = lazy(() => import('./pages/FetchBestPracticesContent').then(m => ({ default: m.FetchBestPracticesContent || m.default })));
const BlogDesignReference = lazy(() => import('./pages/BlogDesignReference').then(m => ({ default: m.BlogDesignReference || m.default })));

// Bundles Page
const BundlesDashboard = lazy(() => import('./pages/BundlesDashboard').then(m => ({ default: m.BundlesDashboard || m.default })));
const BundlesHeroEditor = lazy(() => import('./pages/BundlesHeroEditor').then(m => ({ default: m.BundlesHeroEditor || m.default })));
const BundlesSelectorEditor = lazy(() => import('./pages/BundlesSelectorEditor').then(m => ({ default: m.BundlesSelectorEditor || m.default })));
const BundlesPricingCardsEditor = lazy(() => import('./pages/BundlesPricingCardsEditor').then(m => ({ default: m.BundlesPricingCardsEditor || m.default })));
const BundlesSmartInvestmentEditor = lazy(() => import('./pages/BundlesSmartInvestmentEditor').then(m => ({ default: m.BundlesSmartInvestmentEditor || m.default })));
const BundlesTestimonialsPreview = lazy(() => import('./pages/BundlesTestimonialsPreview').then(m => ({ default: m.BundlesTestimonialsPreview || m.default })));
const BundlesTrustBadgesEditor = lazy(() => import('./pages/BundlesTrustBadgesEditor').then(m => ({ default: m.BundlesTrustBadgesEditor || m.default })));
const BundlesExitIntentEditor = lazy(() => import('./pages/BundlesExitIntentEditor').then(m => ({ default: m.BundlesExitIntentEditor || m.default })));

// Process Page
const ProcessPageDashboard = lazy(() => import('./pages/ProcessPageDashboard').then(m => ({ default: m.ProcessPageDashboard || m.default })));
const ProcessHeroEditor = lazy(() => import('./pages/process/ProcessHeroEditor').then(m => ({ default: m.ProcessHeroEditor || m.default })));
const ProcessMetricsEditor = lazy(() => import('./pages/process/ProcessMetricsEditor').then(m => ({ default: m.ProcessMetricsEditor || m.default })));
const ProcessStepsEditor = lazy(() => import('./pages/process/ProcessStepsEditor').then(m => ({ default: m.ProcessStepsEditor || m.default })));
const ProcessWhySixStepsEditor = lazy(() => import('./pages/process/ProcessWhySixStepsEditor').then(m => ({ default: m.ProcessWhySixStepsEditor || m.default })));
const ProcessGuaranteeEditor = lazy(() => import('./pages/process/ProcessGuaranteeEditor').then(m => ({ default: m.ProcessGuaranteeEditor || m.default })));
const ProcessFinalCTAEditor = lazy(() => import('./pages/process/ProcessFinalCTAEditor').then(m => ({ default: m.ProcessFinalCTAEditor || m.default })));
const ProcessFAQEditor = lazy(() => import('./pages/process/ProcessFAQEditor').then(m => ({ default: m.ProcessFAQEditor || m.default })));
const ProcessTestimonialsPreview = lazy(() => import('./pages/process/ProcessTestimonialsPreview').then(m => ({ default: m.ProcessTestimonialsPreview || m.default })));
const SyncStatusDashboard = lazy(() => import('./pages/SyncStatusDashboard').then(m => ({ default: m.SyncStatusDashboard || m.default })));

// Forms & Email CMS
const TestRoute = lazy(() => import('./pages/TestRoute').then(m => ({ default: m.TestRoute || m.default })));
const FaviconEditor = lazy(() => import('./pages/FaviconEditor').then(m => ({ default: m.FaviconEditor || m.default })));
const FormsDashboard = lazy(() => import('./pages/FormsDashboard').then(m => ({ default: m.FormsDashboard || m.default })));
const FormsSubmissions = lazy(() => import('./pages/FormsSubmissions').then(m => ({ default: m.FormsSubmissions || m.default })));
const EmailTemplates = lazy(() => import('./pages/EmailTemplates').then(m => ({ default: m.EmailTemplates || m.default })));
const CreateBundlePricingTemplate = lazy(() => import('./pages/CreateBundlePricingTemplate').then(m => ({ default: m.CreateBundlePricingTemplate || m.default })));
const TemplateDiagnostic = lazy(() => import('./pages/TemplateDiagnostic').then(m => ({ default: m.TemplateDiagnostic || m.default })));
const TemplateDiagnosticSingle = lazy(() => import('./pages/TemplateDiagnosticSingle').then(m => ({ default: m.TemplateDiagnosticSingle || m.default })));
const NuclearTemplateFix = lazy(() => import('./pages/NuclearTemplateFix').then(m => ({ default: m.NuclearTemplateFix || m.default })));
const EmailSettings = lazy(() => import('./pages/EmailSettings').then(m => ({ default: m.EmailSettings || m.default })));
const EmailLogs = lazy(() => import('./pages/EmailLogs').then(m => ({ default: m.EmailLogs || m.default })));
const LeadsDashboard = lazy(() => import('./pages/LeadsDashboard').then(m => ({ default: m.LeadsDashboard || m.default })));
const SubscribersDashboard = lazy(() => import('./pages/SubscribersDashboard').then(m => ({ default: m.SubscribersDashboard || m.default })));

// Contact Page
const ContactPageDashboard = lazy(() => import('./pages/ContactPageDashboard').then(m => ({ default: m.ContactPageDashboard || m.default })));
const ContactHeroEditor = lazy(() => import('./pages/ContactHeroEditor').then(m => ({ default: m.ContactHeroEditor || m.default })));
const ContactFormSectionEditor = lazy(() => import('./pages/ContactFormSectionEditor').then(m => ({ default: m.ContactFormSectionEditor || m.default })));

// ROI Calculator CMS
const ROICalculatorDashboard = lazy(() => import('./pages/ROICalculatorDashboard').then(m => ({ default: m.ROICalculatorDashboard || m.default })));
const ROIHeroEditor = lazy(() => import('./pages/ROIHeroEditor').then(m => ({ default: m.ROIHeroEditor || m.default })));
const ROICTAEditor = lazy(() => import('./pages/ROICTAEditor').then(m => ({ default: m.ROICTAEditor || m.default })));
const ROIBenchmarksEditor = lazy(() => import('./pages/ROIBenchmarksEditor').then(m => ({ default: m.ROIBenchmarksEditor || m.default })));
const ROIReportsDashboard = lazy(() => import('./pages/ROIReportsDashboard').then(m => ({ default: m.ROIReportsDashboard || m.default })));
const ROIPDFTemplateEditor = lazy(() => import('./pages/ROIPDFTemplateEditor').then(m => ({ default: m.ROIPDFTemplateEditor || m.default })));

// Service Pages
const ExplainerVideoDashboard = lazy(() => import('./pages/ExplainerVideoDashboard').then(m => ({ default: m.ExplainerVideoDashboard || m.default })));
const ServicePagesOverview = lazy(() => import('./pages/ServicePagesOverview').then(m => ({ default: m.ServicePagesOverview || m.default })));
const ServicePagesHub = lazy(() => import('./pages/ServicePagesHub').then(m => ({ default: m.ServicePagesHub || m.default })));
const ServiceHeroEditor = lazy(() => import('./pages/ServiceHeroEditor').then(m => ({ default: m.ServiceHeroEditor || m.default })));
const ServiceStrategicValueEditor = lazy(() => import('./pages/ServiceStrategicValueEditor').then(m => ({ default: m.ServiceStrategicValueEditor || m.default })));
const ServiceServicesEditor = lazy(() => import('./pages/ServiceServicesEditor').then(m => ({ default: m.ServiceServicesEditor || m.default })));
const ServiceIndustriesEditor = lazy(() => import('./pages/ServiceIndustriesEditor').then(m => ({ default: m.ServiceIndustriesEditor || m.default })));
const ServiceVideoPlannerCTAEditor = lazy(() => import('./pages/ServiceVideoPlannerCTAEditor').then(m => ({ default: m.ServiceVideoPlannerCTAEditor || m.default })));
const ExplainerVideosLogoPreview = lazy(() => import('./pages/ExplainerVideosLogoPreview').then(m => ({ default: m.ExplainerVideosLogoPreview || m.default })));
const ExplainerVideosTestimonialsPreview = lazy(() => import('./pages/ExplainerVideosTestimonialsPreview').then(m => ({ default: m.ExplainerVideosTestimonialsPreview || m.default })));
const ExplainerVideosWhyChoosePreview = lazy(() => import('./pages/ExplainerVideosWhyChoosePreview').then(m => ({ default: m.ExplainerVideosWhyChoosePreview || m.default })));
const ExplainerVideosBlogPreview = lazy(() => import('./pages/ExplainerVideosBlogPreview').then(m => ({ default: m.ExplainerVideosBlogPreview || m.default })));
const ExplainerVideosFaqPreview = lazy(() => import('./pages/ExplainerVideosFaqPreview').then(m => ({ default: m.ExplainerVideosFaqPreview || m.default })));
const ExplainerVideosBundlePricingPreview = lazy(() => import('./pages/ExplainerVideosBundlePricingPreview').then(m => ({ default: m.ExplainerVideosBundlePricingPreview || m.default })));

// Industry Pages
const IndustryPagesOverview = lazy(() => import('./pages/IndustryPagesOverview').then(m => ({ default: m.IndustryPagesOverview || m.default })));
const IndustryEditor = lazy(() => import('./pages/IndustryEditor').then(m => ({ default: m.IndustryEditor || m.default })));
const IndustryHeroEditor = lazy(() => import('./pages/industry/IndustryHeroEditor').then(m => ({ default: m.IndustryHeroEditor || m.default })));
const IndustryTrustedClientsRedirect = lazy(() => import('./pages/industry/IndustryTrustedClientsRedirect').then(m => ({ default: m.IndustryTrustedClientsRedirect || m.default })));
const IndustryLogoPreview = lazy(() => import('./pages/industry/IndustryLogoPreview').then(m => ({ default: m.IndustryLogoPreview || m.default })));
const IndustryStrategicValueEditor = lazy(() => import('./pages/industry/IndustryStrategicValueEditor').then(m => ({ default: m.IndustryStrategicValueEditor || m.default })));
const IndustryOurServicesEditor = lazy(() => import('./pages/industry/IndustryOurServicesEditor').then(m => ({ default: m.IndustryOurServicesEditor || m.default })));
const IndustryStrategicApplicationsEditor = lazy(() => import('./pages/industry/IndustryStrategicApplicationsEditor').then(m => ({ default: m.IndustryStrategicApplicationsEditor || m.default })));
const IndustryPortfolioVideoEditor = lazy(() => import('./pages/industry/IndustryPortfolioVideoEditor').then(m => ({ default: m.IndustryPortfolioVideoEditor || m.default })));
const IndustryTestimonialsEditor = lazy(() => import('./pages/industry/IndustryTestimonialsEditor').then(m => ({ default: m.IndustryTestimonialsEditor || m.default })));
const IndustryPlannerCTAEditor = lazy(() => import('./pages/industry/IndustryPlannerCTAEditor').then(m => ({ default: m.IndustryPlannerCTAEditor || m.default })));
const IndustryBlogsEditor = lazy(() => import('./pages/industry/IndustryBlogsEditor').then(m => ({ default: m.IndustryBlogsEditor || m.default })));
const IndustryBundlePricingEditor = lazy(() => import('./pages/industry/IndustryBundlePricingEditor').then(m => ({ default: m.IndustryBundlePricingEditor || m.default })));
const IndustryBundlePricingPreview = lazy(() => import('./pages/industry/IndustryBundlePricingPreview').then(m => ({ default: m.IndustryBundlePricingPreview || m.default })));
const IndustryFAQsEditor = lazy(() => import('./pages/industry/IndustryFAQsEditor').then(m => ({ default: m.IndustryFAQsEditor || m.default })));
const IndustryFAQsPreview = lazy(() => import('./pages/industry/IndustryFAQsPreview').then(m => ({ default: m.IndustryFAQsPreview || m.default })));
const IndustryTestimonialsPreview = lazy(() => import('./pages/industry/IndustryTestimonialsPreview').then(m => ({ default: m.IndustryTestimonialsPreview || m.default })));
const IndustrySectionOrderDebug = lazy(() => import('./pages/IndustrySectionOrderDebug').then(m => ({ default: m.IndustrySectionOrderDebug || m.default })));

// Migration & Testing
const FixLogoURLs = lazy(() => import('./pages/FixLogoURLs').then(m => ({ default: m.FixLogoURLs || m.default })));
const TestIndustriesAPI = lazy(() => import('./pages/TestIndustriesAPI').then(m => ({ default: m.TestIndustriesAPI || m.default })));
const IndustryMigration = lazy(() => import('./pages/IndustryMigration').then(m => ({ default: m.IndustryMigration || m.default })));
const SeedAutomotiveHero = lazy(() => import('./pages/SeedAutomotiveHero').then(m => ({ default: m.SeedAutomotiveHero || m.default })));
const TestBackend = lazy(() => import('./pages/TestBackend').then(m => ({ default: m.TestBackend || m.default })));
const SlugTestingDashboard = lazy(() => import('./pages/SlugTestingDashboard').then(m => ({ default: m.SlugTestingDashboard || m.default })));
const SlugManagementGuide = lazy(() => import('./pages/SlugManagementGuide').then(m => ({ default: m.SlugManagementGuide || m.default })));
const SlugDiagnostic = lazy(() => import('./pages/SlugDiagnostic').then(m => ({ default: m.SlugDiagnostic || m.default })));
const MigrationDashboard = lazy(() => import('./pages/MigrationDashboard').then(m => ({ default: m.MigrationDashboard || m.default })));
const MigrateExplainerVideos = lazy(() => import('./pages/MigrateExplainerVideos').then(m => ({ default: m.MigrateExplainerVideos || m.default })));
const ProductDemosDashboard = lazy(() => import('./pages/ProductDemosDashboard').then(m => ({ default: m.ProductDemosDashboard || m.default })));
const FixProductDemosSections = lazy(() => import('./pages/FixProductDemosSections').then(m => ({ default: m.FixProductDemosSections || m.default })));
const TestMigrationEndpoints = lazy(() => import('./pages/TestMigrationEndpoints').then(m => ({ default: m.TestMigrationEndpoints || m.default })));
const TestSlugSync = lazy(() => import('./pages/TestSlugSync').then(m => ({ default: m.TestSlugSync || m.default })));
const DiagnoseServiceData = lazy(() => import('./pages/DiagnoseServiceData').then(m => ({ default: m.DiagnoseServiceData || m.default })));
const RepairServiceSections = lazy(() => import('./pages/RepairServiceSections').then(m => ({ default: m.RepairServiceSections || m.default })));
const InitializeExplainerVideos = lazy(() => import('./pages/InitializeExplainerVideos').then(m => ({ default: m.InitializeExplainerVideos || m.default })));
const MediaLibrary = lazy(() => import('./pages/MediaLibrary').then(m => ({ default: m.MediaLibrary || m.default })));
const VideoEditor = lazy(() => import('./pages/VideoEditor').then(m => ({ default: m.VideoEditor || m.default })));

// Content Sections
const TestimonialsEditor = lazy(() => import('./pages/TestimonialsEditor').then(m => ({ default: m.TestimonialsEditor || m.default })));
const FaqEditor = lazy(() => import('./pages/FaqEditor').then(m => ({ default: m.FaqEditor || m.default })));
const BundlePricingEditor = lazy(() => import('./pages/BundlePricingEditor').then(m => ({ default: m.BundlePricingEditor || m.default })));

// Public Pages
const HomePage = lazy(() => import('./pages/HomePage').then(m => ({ default: m.HomePage || m.default })));
const ServicePage = lazy(() => import('./components/ServicePage').then(m => ({ default: m.ServicePage || m.default })));
const IndustryPage = lazy(() => import('./pages/IndustryPage').then(m => ({ default: m.IndustryPage || m.default })));
const BlogCatalog = lazy(() => import('./pages/BlogCatalog').then(m => ({ default: m.BlogCatalog || m.default })));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({ default: m.BlogPost || m.default })));
const CaseStudies = lazy(() => import('./pages/CaseStudies').then(m => ({ default: m.CaseStudies || m.default })));
const CaseStudyDetail = lazy(() => import('./pages/CaseStudyDetail').then(m => ({ default: m.CaseStudyDetail || m.default })));
const LegalPageWrapper = lazy(() => import('./pages/LegalPageWrapper').then(m => ({ default: m.LegalPageWrapper || m.default })));
const NewsletterPage = lazy(() => import('./pages/NewsletterPage').then(m => ({ default: m.NewsletterPage || m.default })));

function RouteDebugger() {
  const location = useLocation();
  
  useEffect(() => {
    // Route debugging logic
  }, [location]);
  
  return null;
}

// Wrapper components to extract URL params with Service Registry support
function ServicePageWrapper() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [resolvedServiceId, setResolvedServiceId] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function resolveSlug() {
      if (!slug) {
        setIsResolving(false);
        return;
      }

      try {
        // Call service registry to resolve slug
        const response = await fetch(
          getApiUrl(`/services/registry/resolve-slug/${slug}`)
        );
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.data) {
            const service = data.data;
            
            // Strip /services/ prefix from currentSlug for comparison
            const normalizedCurrentSlug = service.currentSlug.replace(/^\/services\//, '');
            
            // Check if this is a redirect (old slug → new slug)
            if (normalizedCurrentSlug !== slug) {
              // 301 redirect to new slug
              navigate(`/services/${normalizedCurrentSlug}`, { replace: true });
              return;
            }
            
            // Use the internal serviceId
            setResolvedServiceId(service.id);
          } else {
            // Fallback: use slug as serviceId if not in registry
            setResolvedServiceId(slug);
          }
        } else {
          // Fallback: use slug as serviceId if registry fails
          setResolvedServiceId(slug);
        }
      } catch (error) {
        logger.error('❌ [ServicePageWrapper] Error resolving slug:', error);
        // Fallback: use slug as serviceId on error
        setResolvedServiceId(slug);
      } finally {
        setIsResolving(false);
      }
    }

    resolveSlug();
  }, [slug, navigate, location.pathname]);

  if (isResolving) {
    return <div />; // Return empty div instead of null
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#2F2E5C',
        color: '#fff'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '600px', padding: '40px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
          <h1 style={{ fontSize: '32px', marginBottom: '10px' }}>Service Not Found</h1>
          <p style={{ fontSize: '18px', color: '#aaa' }}>{error}</p>
        </div>
      </div>
    );
  }

  return <ServicePage serviceType={resolvedServiceId || slug || ''} />;
}

function IndustryPageWrapper() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [resolvedIndustryId, setResolvedIndustryId] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const resolveSlug = async () => {
      if (!slug) {
        setError('No industry slug provided');
        setIsResolving(false);
        return;
      }

      try {
        const response = await fetch(
          getApiUrl(`/industries/registry/resolve-slug/${slug}`)
        );

        if (!response.ok) {
          throw new Error(`Industry not found: ${slug}`);
        }

        const result = await response.json();

        if (result.success && result.data) {
          const industry = result.data;
          
          // Strip /industries/ prefix from currentSlug for comparison
          const normalizedCurrentSlug = industry.currentSlug.replace(/^\/industries\//, '');
          
          // Check if this is a redirect (old slug → new slug)
          if (normalizedCurrentSlug !== slug) {
            // 301 redirect to new slug
            navigate(`/industries/${normalizedCurrentSlug}`, { replace: true });
            return;
          }

          setResolvedIndustryId(industry.id);
        } else {
          throw new Error('Invalid registry response');
        }
      } catch (err: any) {
        logger.error('❌ [IndustryPageWrapper] Error resolving slug:', err);
        setError(err.message);
      } finally {
        setIsResolving(false);
      }
    };

    resolveSlug();
  }, [slug, navigate]);

  if (isResolving) {
    return <div />; // Return empty div instead of null
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: '#2F2E5C',
        color: '#fff'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '600px', padding: '40px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
          <h1 style={{ fontSize: '32px', marginBottom: '10px' }}>Industry Not Found</h1>
          <p style={{ fontSize: '18px', color: '#aaa' }}>{error}</p>
        </div>
      </div>
    );
  }

  return <IndustryPage industry={resolvedIndustryId || slug || ''} />;
}

export default function App() {
  useEffect(() => {
    // App initialization
  }, []);

 return (
    <HelmetProvider>
      <BrowserRouter>
        <GTMScript />
        <CookieConsentBanner />
        <Toaster position="top-center" richColors closeButton expand={true} />
        <BackendStatusAlert />
        <RouteDebugger />
        <Suspense fallback={<div />}>
        <Routes>
          {/* Debug Route */}
          <Route path="/debug/section-order" element={<IndustrySectionOrderDebug />} />
        
        {/* Admin Routes (No Header/Footer) - Must be first and most specific */}
        <Route path="/admin/setup-first-user" element={<AdminSetup />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/signup" element={<AdminSignup />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<UserManagementPage />} />
        {/* 🔬 Full Website Diagnostics */}
        <Route path="/admin/full-diagnostics" element={<FullDiagnostics />} />
        <Route path="/admin/menu" element={<MenuCMSEditor />} /> {/* Menu/Header CMS Editor */}
        <Route path="/admin/footer" element={<FooterCMSEditor />} /> {/* Footer CMS Editor */}
<Route path="/admin/legal-pages" element={<LegalPagesCMSEditor />} /> {/* Legal Pages CMS Editor */}
<Route path="/admin/legal-pages-cleanup" element={<LegalPagesCleanup />} /> {/* Legal Pages Cleanup Utility */}
<Route path="/admin/terms-debug" element={<TermsPageDebug />} /> {/* Terms Page Debug Utility */}
        {/* <Route path="/admin/migrate" element={<DataMigration />} /> */} {/* Old general migration - DISABLED */}
{/* Blog CMS - Full System */}
<Route path="/admin/blogs" element={<BlogDashboard />} /> {/* Blog Posts Management Dashboard */}
<Route path="/admin/blogs/design-reference" element={<BlogDesignReference />} /> {/* Blog Design Reference - Full hardcoded design */}
<Route path="/admin/blogs/new" element={<BlogEditorNew />} /> {/* Create new blog post */}
<Route path="/admin/blogs/:id" element={<BlogEditorNew />} /> {/* Edit existing blog post */}
<Route path="/admin/taxonomies" element={<TaxonomyManager />} /> {/* Taxonomy Manager - Industries, Services, Tags */}
<Route path="/admin/test-blog-blocks" element={<TestBlogBlocksFetch />} /> {/* Test blog blocks fetch */}
<Route path="/admin/fetch-best-practices" element={<FetchBestPracticesContent />} /> {/* Fetch best practices content */}
        <Route path="/admin/videos/new" element={<VideoEditor />} />
        <Route path="/admin/videos/edit/:id" element={<VideoEditor />} />
        <Route path="/admin/homepage" element={<HomepageDashboard />} />
        <Route path="/admin/explainer-videos" element={<ExplainerVideoDashboard />} /> {/* Advanced dashboard with analytics */}
        <Route path="/admin/homepage/hero" element={<HeroEditor />} />
        <Route path="/admin/homepage/logos" element={<TrustedClientsRedirect />} /> {/* Opens modal instead of page */}
        <Route path="/admin/homepage/logopreview" element={<HomepageLogoPreview />} /> {/* Preview page for homepage */}
        <Route path="/admin/content/trusted-clients" element={<LogoEditor />} /> {/* FULL EDITABLE page in Content section */}
        <Route path="/admin/homepage/logo-editor" element={<LogoEditor />} /> {/* Direct link to editor */}
        <Route path="/admin/homepage/why-choose" element={<WhyChooseUsRedirect />} /> {/* Opens preview page */}
        <Route path="/admin/homepage/whychoosepreview" element={<HomepageWhyChoosePreview />} /> {/* Preview page for homepage */}
        <Route path="/admin/content/why-choose-us" element={<WhyChooseEditor />} /> {/* FULL EDITABLE page in Content section */}
        <Route path="/admin/homepage/bundle-pricing" element={<HomepageBundlePricingPreview />} /> {/* Bundle Pricing preview modal for homepage */}
        <Route path="/admin/homepage/services" element={<ServicesEditor />} />
        <Route path="/admin/homepage/real-world-impact" element={<RealWorldImpactEditor />} />
        <Route path="/admin/homepage/roi-calculator" element={<ROICalculatorEditor />} />
        <Route path="/admin/homepage/how-we-work" element={<HowWeWorkEditor />} />
        <Route path="/admin/homepage/industry-expertise" element={<IndustryExpertiseEditor />} />
        <Route path="/admin/homepage/blog-preview" element={<BlogPreviewEditor />} />
        <Route path="/admin/homepage/video-planner" element={<VideoPlannerEditor />} />
        <Route path="/admin/homepage/faq" element={<HomepageFaqPreview />} /> {/* FAQ preview modal - edit in Content section */}
        <Route path="/admin/homepage/section-order" element={<HomepageSectionOrderEditor />} />
        
        
        
        {/* About Us Page */}
        <Route path="/admin/about-us" element={<AboutUsDashboard />} />
        <Route path="/admin/about-us/hero" element={<AboutHeroEditor />} />
        <Route path="/admin/about-us/trusted-clients" element={<AboutTrustedClientsRedirect />} /> {/* Redirects to logopreview */}
        <Route path="/admin/about-us/logopreview" element={<AboutLogoPreview />} /> {/* About Us Logo Preview */}
        <Route path="/admin/about-us/story-overview" element={<AboutStoryOverviewEditor />} />
        <Route path="/admin/about-us/story-journey" element={<AboutStoryJourneyEditor />} />
        <Route path="/admin/about-us/story-mission-vision" element={<AboutStoryMissionVisionEditor />} />
        <Route path="/admin/about-us/story-core-values" element={<AboutStoryCoreValuesEditor />} />
        <Route path="/admin/about-us/people-overview" element={<AboutPeopleOverviewEditor />} />
        <Route path="/admin/about-us/people-expertise" element={<AboutPeopleExpertiseEditor />} />
        <Route path="/admin/about-us/people-culture" element={<AboutPeopleCultureEditor />} />
        <Route path="/admin/about-us/testimonials-preview" element={<AboutTestimonialsPreview />} />
        <Route path="/admin/about-us/testimonials" element={<AboutTestimonialsRedirect />} /> {/* Add this line */}
        <Route path="/admin/about-us/work-process" element={<AboutWorkProcessEditor />} />
        <Route path="/admin/about-us/work-benefits" element={<AboutWorkBenefitsEditor />} />
        <Route path="/admin/about-us/video-planner-preview" element={<AboutVideoPlannerPreview />} />
        <Route path="/admin/about-us/blog-preview" element={<AboutBlogPreview />} />
        <Route path="/admin/about-us/faqs" element={<AboutFaqPreview />} />
        
        {/* Bundles Page */}
        <Route path="/admin/bundles" element={<BundlesDashboard />} />
        <Route path="/admin/bundles/hero" element={<BundlesHeroEditor />} />
        <Route path="/admin/bundles/selector" element={<BundlesSelectorEditor />} />
        <Route path="/admin/bundles/pricing" element={<BundlesPricingCardsEditor />} />
        <Route path="/admin/bundles/smart-investment" element={<BundlesSmartInvestmentEditor />} />
        <Route path="/admin/bundles/testimonials-preview" element={<BundlesTestimonialsPreview />} />
        <Route path="/admin/bundles/trust-badges" element={<BundlesTrustBadgesEditor />} />
        <Route path="/admin/bundles/exit-intent" element={<BundlesExitIntentEditor />} />
        
        {/* Process Page */}
        <Route path="/admin/process-page" element={<ProcessPageDashboard />} />
        <Route path="/admin/process-page/hero" element={<ProcessHeroEditor />} />
        <Route path="/admin/process-page/metrics" element={<ProcessMetricsEditor />} />
        <Route path="/admin/process-page/steps" element={<ProcessStepsEditor />} />
        <Route path="/admin/process-page/why-six-steps" element={<ProcessWhySixStepsEditor />} />
        <Route path="/admin/process-page/guarantee" element={<ProcessGuaranteeEditor />} />
        <Route path="/admin/process-page/final-cta" element={<ProcessFinalCTAEditor />} />
        <Route path="/admin/process-page/faq" element={<ProcessFAQEditor />} />
        <Route path="/admin/process-page/testimonials-preview" element={<ProcessTestimonialsPreview />} />
        <Route path="/admin/process-page/sync-status" element={<SyncStatusDashboard />} />
        
        {/* Forms & Email CMS (Working Version) */}
        <Route path="/admin/test-route" element={<TestRoute />} />
        <Route path="/admin/forms" element={<FormsDashboard />} />
        <Route path="/admin/forms/submissions" element={<FormsSubmissions />} />
        <Route path="/admin/forms/templates" element={<EmailTemplates />} />
        <Route path="/admin/forms/create-bundle-pricing" element={<CreateBundlePricingTemplate />} />
        <Route path="/admin/forms/diagnostic" element={<TemplateDiagnostic />} />
        <Route path="/admin/forms/diagnostic-single" element={<TemplateDiagnosticSingle />} />
        <Route path="/admin/forms/nuclear-fix" element={<NuclearTemplateFix />} />
        <Route path="/admin/forms/settings" element={<EmailSettings />} />
        <Route path="/admin/forms/logs" element={<EmailLogs />} />
        {/* Leads & Subscribers */}
        <Route path="/admin/leads" element={<LeadsDashboard />} />
        <Route path="/admin/subscribers" element={<SubscribersDashboard />} />
        
        <Route path="/admin/favicon" element={<FaviconEditor />} />
        
        {/* Video Examples CMS */}
        <Route path="/admin/video-examples" element={<VideoExamplesDashboard />} />
        <Route path="/admin/video-examples/page" element={<VideoExamplesPage />} /> {/* NEW CMS-powered page */}
        
        {/* Contact Page */}
        <Route path="/admin/contact" element={<ContactPageDashboard />} />
        <Route path="/admin/contact/hero" element={<ContactHeroEditor />} />
        <Route path="/admin/contact/form-section" element={<ContactFormSectionEditor />} />
        
        {/* ROI Calculator CMS */}
        <Route path="/admin/roi-calculator" element={<ROICalculatorDashboard />} />
        <Route path="/admin/roi-calculator/hero" element={<ROIHeroEditor />} />
        <Route path="/admin/roi-calculator/cta" element={<ROICTAEditor />} />
        <Route path="/admin/roi-calculator/benchmarks" element={<ROIBenchmarksEditor />} />
        <Route path="/admin/roi-calculator/reports" element={<ROIReportsDashboard />} />
        <Route path="/admin/roi-calculator/pdf-template" element={<ROIPDFTemplateEditor />} />
        
        {/* Tracking & Analytics */}
        <Route path="/admin/tracking" element={<TrackingSettingsDashboard />} />
        <Route path="/admin/custom-scripts" element={<CustomScriptsManager />} />
        
        {/* SEO Dashboard */}
        <Route path="/admin/seo" element={<SEODashboard />} />
        
        <Route path="/admin/promo-bar" element={<PromoBarEditor />} />

        
        {/* Migration Dashboard */}
        <Route path="/admin/migrate-explainer-videos" element={<MigrateExplainerVideos />} />
        
        {/* 🔥 SERVICE PAGES OVERVIEW - Shows all services */}
        <Route path="/admin/services" element={<ServicePagesHub />} />
        
        {/* 🔥 UNIVERSAL SERVICE DASHBOARD - Works for ALL services! */}
        <Route path="/admin/services/:serviceType" element={<ExplainerVideoDashboard />} />
        
        {/* 🔥 UNIVERSAL DYNAMIC EDITORS - Work for ALL services (explainer-videos, product-demos, corporate-videos, training-videos)! */}
        <Route path="/admin/services/:serviceType/hero" element={<ServiceHeroEditor />} />
        <Route path="/admin/services/:serviceType/strategic-value" element={<ServiceStrategicValueEditor />} />
        <Route path="/admin/services/:serviceType/video-types" element={<ServiceServicesEditor />} />
        <Route path="/admin/services/:serviceType/industries" element={<ServiceIndustriesEditor />} />
        <Route path="/admin/services/:serviceType/planner-cta" element={<ServiceVideoPlannerCTAEditor />} />
        
        {/* 🔥 UNIVERSAL PREVIEW MODALS - Work for ALL services! */}
        <Route path="/admin/services/:serviceType/logopreview" element={<ExplainerVideosLogoPreview />} />
        <Route path="/admin/services/:serviceType/testimonials" element={<ExplainerVideosTestimonialsPreview />} />
        <Route path="/admin/services/:serviceType/whychoosepreview" element={<ExplainerVideosWhyChoosePreview />} />
        <Route path="/admin/services/:serviceType/blogpreview" element={<ExplainerVideosBlogPreview />} />
        <Route path="/admin/services/:serviceType/faqpreview" element={<ExplainerVideosFaqPreview />} />
        <Route path="/admin/services/:serviceType/bundle-pricing" element={<ExplainerVideosBundlePricingPreview />} />
        
        {/* 🏭 INDUSTRY PAGES */}
        <Route path="/admin/industry-pages" element={<IndustryPagesOverview />} />
        <Route path="/admin/industry-pages/:industryId" element={<IndustryEditor />} />
        
        {/* 🏭 INDUSTRY PAGE SECTION EDITORS */}
        <Route path="/admin/industry-pages/:industryId/hero" element={<IndustryHeroEditor />} />
        <Route path="/admin/industry-pages/:industryId/trusted-clients" element={<IndustryTrustedClientsRedirect />} />
        <Route path="/admin/industry-pages/:industryId/logopreview" element={<IndustryLogoPreview />} />
        <Route path="/admin/industry-pages/:industryId/strategic-value" element={<IndustryStrategicValueEditor />} />
        <Route path="/admin/industry-pages/:industryId/our-services" element={<IndustryOurServicesEditor />} />
        <Route path="/admin/industry-pages/:industryId/strategic-applications" element={<IndustryStrategicApplicationsEditor />} />
        <Route path="/admin/industry-pages/:industryId/portfolio-video" element={<IndustryPortfolioVideoEditor />} />
        <Route path="/admin/industry-pages/:industryId/testimonials" element={<IndustryTestimonialsEditor />} />
        <Route path="/admin/industry-pages/:industryId/planner-cta" element={<IndustryPlannerCTAEditor />} />
        <Route path="/admin/industry-pages/:industryId/blogs" element={<IndustryBlogsEditor />} />
        <Route path="/admin/industry-pages/:industryId/bundle-pricing" element={<IndustryBundlePricingEditor />} />
        <Route path="/admin/industry-pages/:industryId/bundle-pricing-preview" element={<IndustryBundlePricingPreview />} />
        <Route path="/admin/industry-pages/:industryId/faqs" element={<IndustryFAQsEditor />} />
        <Route path="/admin/industry-pages/:industryId/faqs-preview" element={<IndustryFAQsPreview />} />
        <Route path="/admin/industry-pages/:industryId/testimonials-preview" element={<IndustryTestimonialsPreview />} />
        
        {/* Legacy fix pages - can be removed after migration */}
        <Route path="/admin/services/product-demos/fix-sections" element={<FixProductDemosSections />} />
        
        <Route path="/admin/content/testimonials" element={<TestimonialsEditor />} /> {/* FULL EDITABLE page in Content section */}
        <Route path="/admin/content/faqs" element={<FaqEditor />} /> {/* FULL EDITABLE FAQs in Content section */}
        <Route path="/admin/content/bundle-pricing" element={<BundlePricingEditor />} /> {/* FULL EDITABLE Bundle Pricing in Content section */}
        {/* Homepage Preview Modals for Global Sections */}
        <Route path="/admin/homepage/faqpreview" element={<HomepageFaqPreview />} /> {/* FAQ preview modal for homepage */}
        <Route path="/admin/homepage/bundlepricing-preview" element={<HomepageBundlePricingPreview />} /> {/* Bundle Pricing preview modal for homepage */}
        {/* Other Admin Routes */}
        <Route path="/admin/fix-logo-urls" element={<FixLogoURLs />} />
        <Route path="/admin/test-industries" element={<TestIndustriesAPI />} />
        <Route path="/admin/test-backend" element={<TestBackend />} />
        <Route path="/admin/test-migration" element={<TestMigrationEndpoints />} />
        <Route path="/admin/slug-testing" element={<SlugTestingDashboard />} />
<Route path="/admin/slug-guide" element={<SlugManagementGuide />} />
<Route path="/admin/slug-diagnostic" element={<SlugDiagnostic />} />
        <Route path="/admin/diagnose-services" element={<DiagnoseServiceData />} />
        <Route path="/admin/media" element={<MediaLibrary />} />
        <Route path="/admin/setup" element={<AdminSetup />} />
        <Route path="/admin/repair-sections" element={<RepairServiceSections />} />
        <Route path="/admin/industry-migration" element={<IndustryMigration />} />
        <Route path="/admin/seed-automotive-hero" element={<SeedAutomotiveHero />} />
        <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
        

        {/* Public Routes (With Header/Footer) */}
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/videos" element={<VideoExamplesPage />} /> {/* ✅ CMS-powered version */}
          <Route path="/blog" element={<BlogCatalog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          <Route path="/case-studies/:id" element={<CaseStudyDetail />} />
          {/* 🔥 REMOVED HARDCODED PAGE ROUTES - Now handled dynamically by DynamicPageRouter
          <Route path="/roi" element={<ROIPage />} />
          <Route path="/about" element={<AboutPageNew />} />
          <Route path="/process" element={<ProcessPage />} />
          <Route path="/contact" element={<ContactPage />} />
          */}
          {/* 🔥 REMOVED HARDCODED ROUTES - All services now use dynamic CMS structure */}
          {/* <Route path="/services/explainer-videos" element={<ExplainerVideos />} /> */}
          {/* <Route path="/services/product-demos" element={<ProductDemos />} /> */}
          {/* 🔥 UNIVERSAL DYNAMIC SERVICE ROUTE - Works for ALL services! */}
          <Route path="/services/:slug" element={<ServicePageWrapper />} />
          <Route path="/industries/:slug" element={<IndustryPageWrapper />} />
          {/* <Route path="/bundles" element={<BundlesPage />} /> */}
          {/* <Route path="/privacy" element={<LegalPageWrapper />} /> */}
{/* <Route path="/terms" element={<LegalPageWrapper />} /> */}
{/* <Route path="/cookies" element={<LegalPageWrapper />} /> */}
<Route path="/cookie-policy" element={<LegalPageWrapper />} />
<Route path="/privacy-policy" element={<LegalPageWrapper />} />
<Route path="/terms-of-service" element={<LegalPageWrapper />} />
          <Route path="/newsletter" element={<NewsletterPage />} />
          <Route path="/media-library" element={<MediaLibrary />} />
          <Route path="/initialize-explainer-videos" element={<InitializeExplainerVideos />} />
          {/* 🎯 DYNAMIC SLUG ROUTER - Matches ANY slug and resolves to correct page */}
{/* This MUST be last to act as catch-all for dynamic slugs */}
<Route path="/:slug" element={<DynamicPageRouter />} />
        </Route>
        
        {/* Catch-all for unknown routes - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  </HelmetProvider>
  );
}